import { useEffect, useCallback } from 'react';

/**
 * Hook for handling page-level hotkey navigation.
 * Use this for category/tab navigation within a page (e.g., Stack, Articles).
 *
 * @param {Array<{id: string, hotkey: string}>} items - Items with hotkey mappings
 * @param {(id: string) => void} onSelect - Callback when a hotkey is pressed
 * @param {Object} options - Optional configuration
 * @param {boolean} options.enabled - Whether hotkeys are active (default: true)
 */
export function usePageHotkeys(items, onSelect, options = {}) {
  const { enabled = true } = options;

  const handleKeyDown = useCallback(
    event => {
      if (!enabled) return;

      // Skip if user is typing in an input, textarea, or contenteditable
      const target = event.target;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      // Skip if modifier keys are pressed (allow browser shortcuts)
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }

      const key = event.key.toUpperCase();
      const item = items.find(i => i.hotkey?.toUpperCase() === key);

      if (item) {
        event.preventDefault();
        onSelect(item.id);
      }
    },
    [items, onSelect, enabled]
  );

  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown, enabled]);
}
